import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import {
  SENSOR_ACTION_SIDES,
  type SensorActionSide,
  SENSOR_TRIGGER_NAMES,
  SENSOR_TRIGGER_ARG_KEYS,
} from '../../../CodeEditor/ConfigurationTabs/sensorMappingConstants';
import ListHeader from './ListHeader';
import ListRow, { PROTECTED_FILL_COLUMN_TEMPLATE } from './ListRow';
import MappingListGrid from './MappingListGrid';
import { iconButtonCell } from './listCellUtils';
import type { SensorMappingDraftLike, SensorTriggerDraftLike } from './sensorMappingTypes';

type SensorTriggerRow = {
  sensorVarName: string;
  triggerIndex: number;
  actionIndex: number;
  side: SensorActionSide;
  triggerName: string;
  triggerArgs: Record<string, string>;
  rawTriggerArgsText?: string;
  actionLabel: string;
  argValues: Record<string, string>;
  rawExpression?: string;
};

type TFn = (key: string, options?: Record<string, unknown>) => string;

export interface SensorMappingControlListProps {
  sensorVarNames: string[];
  draftsBySensorVar: Record<string, SensorMappingDraftLike | undefined>;
  onAddSensorMapping: (sensorVarName: string) => void;
  onEditSensor: (sensorVarName: string, triggerIndex: number, side?: SensorActionSide, actionIndex?: number) => void;
  onDeleteSensorMapping: (sensorVarName: string, triggerIndex: number, side: SensorActionSide, actionIndex: number) => void;
}

function formatKwargs(args: Record<string, string> | undefined): string {
  const entries = Object.entries(args ?? {})
    .filter(([k, v]) => String(k || '').trim() && String(v ?? '').trim())
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  if (entries.length === 0) return '';
  return entries.map(([k, v]) => `${k}=${String(v).trim()}`).join(', ');
}

function formatSensorAction(label: string, args: Record<string, string> | undefined, t: TFn): string {
  const action = String(label || '').trim();
  if (!action) return t('controller.buttonMapping.none', { defaultValue: 'None' });
  const kwargs = formatKwargs(args);
  return kwargs ? `${action}(${kwargs})` : `${action}()`;
}

function getActionsForSide(
  tr: SensorTriggerDraftLike | undefined,
  actionsKey: 'onTrueActions' | 'onFalseActions',
  legacyLabelKey: 'onTrueActionLabel' | 'onFalseActionLabel',
  legacyArgsKey: 'onTrueArgValues' | 'onFalseArgValues'
): Array<{ actionLabel: string; argValues: Record<string, string>; rawExpression?: string }> {
  const actions = tr?.[actionsKey];
  if (Array.isArray(actions)) {
    return actions
      .map((action) => ({
        actionLabel: String(action?.actionLabel ?? ''),
        argValues: (action?.argValues ?? {}) as Record<string, string>,
        rawExpression: String(action?.rawExpression ?? '').trim() || undefined,
      }))
      .filter((action) => action.actionLabel.trim() || action.rawExpression);
  }

  const actionLabel = String(tr?.[legacyLabelKey] ?? '');
  if (!actionLabel.trim()) return [];
  return [
    {
      actionLabel,
      argValues: (tr?.[legacyArgsKey] ?? {}) as Record<string, string>,
    },
  ];
}

function formatSensorActionForRow(row: SensorTriggerRow, t: TFn): string {
  const rawExpression = String(row.rawExpression || '').trim();
  if (rawExpression) {
    return `${t('controlPanel.sensors.mapping.unsupportedAction.label', { defaultValue: 'Unsupported custom code' })}: ${rawExpression}`;
  }
  return formatSensorAction(row.actionLabel, row.argValues, t);
}

function formatSensorTriggerDetailForRow(row: SensorTriggerRow, t: TFn): string {
  const n = String(row.triggerName || '').trim();
  if (!n) return '';
  const rawTriggerArgsText = String(row.rawTriggerArgsText || '').trim();
  if (rawTriggerArgsText) {
    return `${t('controlPanel.sensors.mapping.unsupportedTriggerArgs.label', { defaultValue: 'Custom code' })}: ${n}(${rawTriggerArgsText}) • ${
      row.side
    }`;
  }

  // Beam break (and similar boolean sensors): show only which side this row represents.
  if (n === SENSOR_TRIGGER_NAMES.BEAM_BROKEN || n === SENSOR_TRIGGER_NAMES.DETECTED) {
    return row.side;
  }

  // Distance sensor triggers: show parameter values compactly.
  if (n === SENSOR_TRIGGER_NAMES.GREATER_THAN) {
    const v = String(row.triggerArgs?.[SENSOR_TRIGGER_ARG_KEYS.DISTANCE] ?? '').trim();
    const base = v ? `> ${v}` : '>';
    return `${base} • ${row.side}`;
  }
  if (n === SENSOR_TRIGGER_NAMES.LESS_THAN) {
    const v = String(row.triggerArgs?.[SENSOR_TRIGGER_ARG_KEYS.DISTANCE] ?? '').trim();
    const base = v ? `< ${v}` : '<';
    return `${base} • ${row.side}`;
  }
  if (n === SENSOR_TRIGGER_NAMES.BETWEEN) {
    const near = String(row.triggerArgs?.[SENSOR_TRIGGER_ARG_KEYS.DISTANCE_NEAR] ?? '').trim();
    const far = String(row.triggerArgs?.[SENSOR_TRIGGER_ARG_KEYS.DISTANCE_FAR] ?? '').trim();
    const base = near && far ? `${near}–${far}` : near || far ? `${near}–${far}` : '–';
    return `${base} • ${row.side}`;
  }

  // Fallback: show trigger name and kwargs.
  const kwargs = formatKwargs(row.triggerArgs);
  const base = kwargs ? `${n}(${kwargs})` : `${n}()`;
  return `${base} • ${row.side}`;
}

function renderSensorTriggerCell(row: SensorTriggerRow): React.ReactNode {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        minWidth: 0,
        width: '100%',
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1.25,
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}
      >
        {row.sensorVarName}
      </Typography>
      <Typography
        component="span"
        sx={{
          color: 'text.secondary',
          fontSize: 12,
          lineHeight: 1.25,
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}
      >
        {row.triggerName}
      </Typography>
    </Box>
  );
}

const SensorMappingControlList: React.FC<SensorMappingControlListProps> = ({
  sensorVarNames,
  draftsBySensorVar,
  onAddSensorMapping,
  onEditSensor,
  onDeleteSensorMapping,
}) => {
  const { t } = useTranslation();
  const addSensorMappingDescription = t('controlPanel.sensors.mapping.description.add', { defaultValue: 'add sensor mapping' });
  const editSensorMappingDescription = t('controlPanel.sensors.mapping.description.edit', { defaultValue: 'edit sensor mapping' });
  const deleteSensorMappingDescription = t('controlPanel.sensors.mapping.description.delete', { defaultValue: 'delete sensor mapping' });

  const rows: SensorTriggerRow[] = useMemo(() => {
    const out: SensorTriggerRow[] = [];
    const sensors = Object.keys(draftsBySensorVar || {}).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
    for (const sensorVarName of sensors) {
      const draft = draftsBySensorVar[sensorVarName];
      const triggers = draft?.triggers ?? [];
      for (let i = 0; i < triggers.length; i++) {
        const tr = triggers[i];
        const triggerName = String(tr?.triggerName ?? '');
        const triggerArgs = (tr?.triggerArgs ?? {}) as Record<string, string>;
        const rawTriggerArgsText = String(tr?.rawTriggerArgsText ?? '').trim() || undefined;
        const onTrueActions = getActionsForSide(tr, 'onTrueActions', 'onTrueActionLabel', 'onTrueArgValues');
        const onFalseActions = getActionsForSide(tr, 'onFalseActions', 'onFalseActionLabel', 'onFalseArgValues');

        onTrueActions.forEach((action, actionIndex) => {
          out.push({
            sensorVarName,
            triggerIndex: i,
            actionIndex,
            side: SENSOR_ACTION_SIDES.ON_TRUE,
            triggerName,
            triggerArgs,
            rawTriggerArgsText,
            actionLabel: action.actionLabel,
            argValues: action.argValues,
            rawExpression: action.rawExpression,
          });
        });
        onFalseActions.forEach((action, actionIndex) => {
          out.push({
            sensorVarName,
            triggerIndex: i,
            actionIndex,
            side: SENSOR_ACTION_SIDES.ON_FALSE,
            triggerName,
            triggerArgs,
            rawTriggerArgsText,
            actionLabel: action.actionLabel,
            argValues: action.argValues,
            rawExpression: action.rawExpression,
          });
        });
      }
    }
    return out;
  }, [draftsBySensorVar]);

  const firstSensorVarName = String(sensorVarNames?.[0] ?? '');
  if ((sensorVarNames || []).length === 0) return null;
  const gridTemplateColumns = `minmax(0, 0.9fr) minmax(0, 0.7fr) ${PROTECTED_FILL_COLUMN_TEMPLATE} 2.5rem 2.5rem`;

  return (
    <MappingListGrid
      scrollTestId="sensor-mapping-list-scroll"
      gridTestId="sensor-mapping-list-grid"
      gridTemplateColumns={gridTemplateColumns}
      sx={{ mt: 2 }}
    >
      <ListHeader
        columns={[
          {
            columnWidth: 'fill',
            label: t('controlPanel.sensors.mapping.list.header.sensorTrigger', { defaultValue: 'Sensor Trigger' }),
            align: 'left',
          },
          {
            columnWidth: 'fill',
            label: t('controlPanel.sensors.mapping.list.header.trigger', { defaultValue: 'Event' }),
            align: 'left',
          },
          {
            columnWidth: 'protectedFill',
            label: t('controlPanel.sensors.mapping.list.header.actions', { defaultValue: 'Actions' }),
            align: 'left',
          },
        ]}
        addAriaLabel={t('controlPanel.sensors.mapping.add.ariaLabel', { defaultValue: 'Add sensor mapping' })}
        addDescription={addSensorMappingDescription}
        addDescriptionId="sensor-mapping-add-description"
        onAdd={() => onAddSensorMapping(firstSensorVarName)}
        AddIcon={AddIcon}
        trailingActionColumnCount={2}
        useParentColumns
      />

      {rows.map((row, idx) => {
        const triggerText = formatSensorTriggerDetailForRow(row, t);
        const actionsText = formatSensorActionForRow(row, t);

        return (
          <React.Fragment key={`${row.sensorVarName}-${row.triggerName}-${row.side}-${row.actionIndex}-${idx}`}>
            <ListRow
              useParentColumns
              cells={[
                {
                  columnWidth: 'fill',
                  align: 'left',
                  content: renderSensorTriggerCell(row),
                  wrapTypography: false,
                },
                { columnWidth: 'fill', align: 'left', content: triggerText, noWrap: false },
                { columnWidth: 'protectedFill', align: 'left', content: actionsText, noWrap: false },
                iconButtonCell({
                  ariaLabel: t('controlPanel.sensors.mapping.edit.ariaLabel', { defaultValue: 'Edit sensor mapping' }),
                  description: editSensorMappingDescription,
                  descriptionId: `sensor-mapping-${idx}-edit-description`,
                  onClick: () => onEditSensor(row.sensorVarName, row.triggerIndex, row.side, row.actionIndex),
                  Icon: EditIcon,
                }),
                iconButtonCell({
                  ariaLabel: t('controlPanel.sensors.mapping.delete.ariaLabel', { defaultValue: 'Delete sensor mapping' }),
                  description: deleteSensorMappingDescription,
                  descriptionId: `sensor-mapping-${idx}-delete-description`,
                  onClick: () => onDeleteSensorMapping(row.sensorVarName, row.triggerIndex, row.side, row.actionIndex),
                  Icon: DeleteIcon,
                }),
              ]}
              rowSx={(theme: Theme) => ({
                px: 2,
                py: 0.5,
                transition: 'background-color 120ms ease',
                '&:hover': { bgcolor: theme.vars?.palette?.action?.hover },
              })}
            />
          </React.Fragment>
        );
      })}
    </MappingListGrid>
  );
};

export default SensorMappingControlList;
